"use client";

import clsx from "clsx";
import type React from "react";
import { useMemo } from "react";
import Chip from "src/components/common/Chip/Chip";
import Snippet from "src/components/common/Snippet/Snippet";
import type { InputProps } from "src/components/form/InputTypes";
import PathParameterField from "src/components/form/PathParameterField/PathParameterField";
import { getMethodColor } from "src/lib/HttpMethodColor";

export interface MethodUrlDisplayProps
  extends InputProps<Record<string, string>>,
    Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "defaultValue" | "onBlur" | "onFocus"> {
  method?: string;
  baseUrl: string;
  path: string;
  className?: string;
}

const MethodUrlDisplay = (props: MethodUrlDisplayProps) => {
  const {
    method,
    baseUrl,
    path,
    className,
    value,
    defaultValue,
    onChange,
    onValueChange,
    name,
    disabled,
    ...otherProps
  } = props;

  const { segments } = useMemo(() => {
    const regex = /:(\w+)/g;
    const segments: Array<{ type: "text" | "dynamic"; content: string; key?: string }> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null = null;

    while ((match = regex.exec(path)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        segments.push({
          type: "text",
          content: path.slice(lastIndex, match.index),
        });
      }

      // Add the dynamic segment
      const key = match[1];
      segments.push({
        type: "dynamic",
        content: match[0],
        key,
      });

      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < path.length) {
      segments.push({
        type: "text",
        content: path.slice(lastIndex),
      });
    }

    // If no segments were found, add the entire path as text
    if (segments.length === 0) {
      segments.push({
        type: "text",
        content: path,
      });
    }

    return { segments };
  }, [path]);

  // Get current values from props or use empty object
  const currentValues = value || defaultValue || {};
  const baseName = name;

  return (
    <div
      className={clsx(
        "flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3",
        className,
      )}
      {...otherProps}
    >
      {method && <Chip color={getMethodColor(method)}>{method}</Chip>}
      <Snippet showCopyButton={true} variant="field">
        <span className="text-muted">{baseUrl}</span>
        {segments.map((segment, index) => {
          if (segment.type === "dynamic" && segment.key) {
            const paramName = baseName ? `${baseName}.${segment.key}` : segment.key;
            return (
              <PathParameterField
                key={index}
                name={paramName}
                defaultValue={currentValues[segment.key]}
                disabled={disabled}
                placeholder={segment.key}
              />
            );
          }
          return <span key={index}>{segment.content}</span>;
        })}
      </Snippet>
    </div>
  );
};

export default MethodUrlDisplay;
